import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";
import { effectiveArticlePrice } from "@/lib/articleConstants";
import { formatIsoDateShort, todayLocal } from "@/lib/bookingDates";
import {
  formatCurrency,
  grossAmountFromNet,
  grossFromNet,
  netAmountFromGross,
  parseAmount,
} from "@/lib/money";
import type { ArticlePrice } from "@/types/camping";

export interface ArticlePricesSectionProps {
  articleId: string;
  taxRatePercent: string;
  canEdit: boolean;
  onSaved?: () => void;
  compact?: boolean;
}

type PriceFormState = { valid_from: string; valid_until: string; amount: string };

function emptyPriceForm(): PriceFormState {
  return { valid_from: todayLocal(), valid_until: "", amount: "" };
}

export function ArticlePricesSection({
  articleId,
  taxRatePercent,
  canEdit,
  onSaved,
  compact = false,
}: ArticlePricesSectionProps) {
  const [prices, setPrices] = useState<ArticlePrice[]>([]);
  const [priceForm, setPriceForm] = useState<PriceFormState>(emptyPriceForm);
  const [editingPrice, setEditingPrice] = useState<ArticlePrice | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const taxRate = Number(taxRatePercent) || 0;
  const today = todayLocal();

  const loadPrices = useCallback((id: string) => {
    setLoading(true);
    api
      .get<ArticlePrice[]>(`/api/v1/articles/${id}/prices`)
      .then(setPrices)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPriceForm(emptyPriceForm());
    setEditingPrice(null);
    setError(null);
    setSubmitting(false);
    loadPrices(articleId);
  }, [articleId, loadPrices]);

  const currentPrice = useMemo(
    () => effectiveArticlePrice(prices, today),
    [prices, today],
  );

  const activeForm = editingPrice ? priceForm : priceForm;
  const previewGross = parseAmount(activeForm.amount);
  const previewNet =
    previewGross != null ? netAmountFromGross(previewGross, taxRate) : null;

  function grossForPrice(price: ArticlePrice): number {
    const net = Number(price.amount);
    return grossAmountFromNet(net, taxRate) ?? grossFromNet(net, taxRate);
  }

  function startEdit(price: ArticlePrice) {
    setEditingPrice(price);
    setPriceForm({
      valid_from: price.valid_from,
      valid_until: price.valid_until ?? "",
      amount: grossForPrice(price).toFixed(2),
    });
    setError(null);
  }

  function cancelEdit() {
    setEditingPrice(null);
    setPriceForm(emptyPriceForm());
    setError(null);
  }

  async function onSavePrice() {
    if (!canEdit) return;
    const gross = parseAmount(priceForm.amount);
    if (gross == null) {
      setError("Bitte einen gültigen Bruttopreis eingeben.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      if (priceForm.valid_until && priceForm.valid_until < priceForm.valid_from) {
        setError("Das Ablaufdatum darf nicht vor dem Beginn liegen.");
        return;
      }
      const payload = {
        valid_from: priceForm.valid_from,
        valid_until: priceForm.valid_until || null,
        gross_amount: gross,
      };
      if (editingPrice) {
        await api.patch(`/api/v1/articles/${articleId}/prices/${editingPrice.id}`, payload);
        cancelEdit();
      } else {
        await api.post(`/api/v1/articles/${articleId}/prices`, payload);
        setPriceForm(emptyPriceForm());
      }
      loadPrices(articleId);
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Preis");
    } finally {
      setSubmitting(false);
    }
  }

  async function onDeletePrice(price: ArticlePrice) {
    if (!canEdit) return;
    const gross = formatCurrency(grossForPrice(price), price.currency);
    const until = price.valid_until ? ` bis ${formatIsoDateShort(price.valid_until)}` : "";
    if (!window.confirm(`Preis ${gross} (ab ${formatIsoDateShort(price.valid_from)}${until}) wirklich löschen?`)) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await api.del(`/api/v1/articles/${articleId}/prices/${price.id}`);
      if (editingPrice?.id === price.id) cancelEdit();
      loadPrices(articleId);
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Löschen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className={clsx("space-y-4", !compact && "rounded-lg border border-separator p-4")}>
      <div>
        <h3 className="text-sm font-semibold text-label">Preise</h3>
        <p className="mt-1 text-xs text-label-secondary">
          Brutto-Preise mit Zeitraum. Ohne Ablaufdatum gilt der Preis, bis ein späterer Preis beginnt.
          Mit Ablaufdatum endet er an diesem Tag. Danach buchen Gäste ohne Preis, bis ein neuer Zeitraum
          hinterlegt ist.
        </p>
      </div>

      {currentPrice ? (
        <div className="rounded-lg bg-fill-tertiary px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-label-secondary">
            Aktueller Preis
          </p>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-lg font-semibold tabular-nums text-label">
              {formatCurrency(
                grossForPrice(currentPrice),
                currentPrice.currency,
              )}
            </span>
            <span className="text-sm text-label-secondary">
              brutto · {taxRate} % MwSt. · netto{" "}
              {formatCurrency(currentPrice.amount, currentPrice.currency)}
            </span>
          </div>
          <p className="mt-1 text-xs text-label-tertiary">
            gültig ab {formatIsoDateShort(currentPrice.valid_from)}
            {currentPrice.valid_until
              ? ` bis ${formatIsoDateShort(currentPrice.valid_until)}`
              : " · ohne Ablaufdatum"}
          </p>
        </div>
      ) : (
        <p className="rounded-lg border border-dashed border-separator px-4 py-3 text-sm text-label-secondary">
          Noch kein Preis hinterlegt. Buchungen sind trotzdem möglich, der Preis wird später
          mitgeteilt.
        </p>
      )}

      {canEdit ? (
        <div className="rounded-lg border border-separator bg-fill-tertiary/50 p-4">
          <p className="mb-3 text-sm font-medium text-label">
            {editingPrice ? "Preis bearbeiten" : "Neuen Preis hinzufügen"}
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              Gültig ab
              <input
                type="date"
                required
                className="eh-input"
                value={priceForm.valid_from}
                onChange={(e) => setPriceForm((f) => ({ ...f, valid_from: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Gültig bis
              <input
                type="date"
                className="eh-input"
                value={priceForm.valid_until}
                onChange={(e) => setPriceForm((f) => ({ ...f, valid_until: e.target.value }))}
              />
              <span className="text-xs text-label-secondary">Leer lassen, wenn der Preis nicht ablaufen soll.</span>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Brutto-Preis (VK)
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  className="eh-input w-full pr-8"
                  placeholder="0,00"
                  value={priceForm.amount}
                  onChange={(e) => setPriceForm((f) => ({ ...f, amount: e.target.value }))}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-label-secondary">
                  €
                </span>
              </div>
            </label>
          </div>

          {previewNet != null ? (
            <p className="mt-2 text-xs text-label-secondary">
              Netto (Buchhaltung):{" "}
              <span className="font-medium tabular-nums text-label">
                {formatCurrency(previewNet)}
              </span>{" "}
              ({taxRate} % MwSt.)
            </p>
          ) : null}

          <div className="mt-3 flex justify-end gap-2">
            {editingPrice ? (
              <Button type="button" variant="secondary" size="sm" onClick={cancelEdit}>
                Abbrechen
              </Button>
            ) : null}
            <Button
              type="button"
              loading={submitting}
              size="sm"
              onClick={() => void onSavePrice()}
            >
              {editingPrice ? "Speichern" : "Preis hinzufügen"}
            </Button>
          </div>
        </div>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Preishistorie laden…</p>
      ) : prices.length === 0 ? null : (
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-label-secondary">
            Preishistorie
          </p>
          <div className="overflow-hidden rounded-lg border border-separator">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-separator bg-fill-tertiary/60 text-xs text-label-secondary">
                  <th className="px-3 py-2 font-medium">Zeitraum</th>
                  <th className="px-3 py-2 font-medium text-right">Brutto</th>
                  <th className="px-3 py-2 font-medium text-right">Netto</th>
                  <th className="px-3 py-2 font-medium text-right">Status</th>
                  {canEdit ? (
                    <th className="px-3 py-2 font-medium text-right">Aktionen</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {prices.map((price) => {
                  const isCurrent = currentPrice?.id === price.id;
                  const isFuture = price.valid_from > today;
                  const isExpired = Boolean(price.valid_until && price.valid_until < today);
                  const isEditing = editingPrice?.id === price.id;
                  return (
                    <tr
                      key={price.id}
                      className={clsx(
                        "border-b border-separator last:border-b-0",
                        (isCurrent || isEditing) && "bg-accent/5",
                      )}
                    >
                      <td className="px-3 py-2 tabular-nums">
                        {formatIsoDateShort(price.valid_from)}
                        {price.valid_until ? ` – ${formatIsoDateShort(price.valid_until)}` : " – offen"}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatCurrency(grossForPrice(price), price.currency)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-label-secondary">
                        {formatCurrency(price.amount, price.currency)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isCurrent ? (
                          <span className="inline-flex rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent">
                            Aktuell
                          </span>
                        ) : isFuture ? (
                          <span className="inline-flex rounded-full bg-fill-tertiary px-2 py-0.5 text-xs text-label-secondary">
                            Geplant
                          </span>
                        ) : isExpired ? (
                          <span className="inline-flex rounded-full bg-fill-tertiary px-2 py-0.5 text-xs text-label-tertiary">
                            Abgelaufen
                          </span>
                        ) : (
                          <span className="text-xs text-label-tertiary">—</span>
                        )}
                      </td>
                      {canEdit ? (
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={submitting}
                              onClick={() => startEdit(price)}
                            >
                              Bearbeiten
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={submitting}
                              onClick={() => void onDeletePrice(price)}
                            >
                              Löschen
                            </Button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
